import { useRef } from 'react';
import { ErrorState } from '@/components/full-page-state';
import { LINKS } from '@/shared/config/links';
import { planName } from '@/shared/config/plans';
import { useEntranceMotion } from '@/shared/hooks/use-entrance-motion';
import { formatCount, formatPodHours } from '@/shared/lib/format';
import { summarizeUsage } from '@/shared/lib/pod-usage';
import { Skeleton } from '@/shared/ui/skeleton';
import { useCheckoutFlow } from '../hooks/use-checkout-flow';
import { useGetPlans } from '../hooks/use-get-plans';
import type { WorkspaceData } from '../types';
import { ActivatingCard } from './activating-card';
import { ActivationTimeoutCard } from './activation-timeout-card';
import { CheckoutSuccessCard } from './checkout-success-card';
import { CheckoutSummaryCard } from './checkout-summary-card';
import { PlanOption } from './plan-option';

type CheckoutViewProps = { workspace: WorkspaceData };

function introText(workspace: WorkspaceData, isChange: boolean): string {
  const usage = summarizeUsage(workspace.usage);
  if (isChange) {
    return `You are on ${planName(workspace.plan)}, ${formatPodHours(workspace.usage.usedPodHours)} of ${formatCount(
      workspace.usage.includedPodHours,
    )} Pod-hours used this month. Moving up starts right away.`;
  }
  if (usage.isFree) {
    return `You are on Free with ${usage.left} of ${usage.limit} Pod-hours left. A plan starts right away, and running Pods keep going.`;
  }
  return 'A plan starts right away, and running Pods keep going.';
}

/** Plans and Paddle checkout (design: Checkout). */
export function CheckoutView({ workspace }: CheckoutViewProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const flow = useCheckoutFlow(workspace);
  const { plans, isLoading, error, refetch } = useGetPlans();
  useEntranceMotion(rootRef, flow.view, { distance: 14, stagger: 0.08 });
  const paidPlans = plans.filter((plan) => plan.id === 'PRO' || plan.id === 'TEAM');
  const name = planName(flow.plan);

  return (
    <div ref={rootRef} className="contents">
      {flow.view === 'activating' ? <ActivatingCard planName={name} /> : null}
      {flow.view === 'timeout' ? <ActivationTimeoutCard planName={name} /> : null}
      {flow.view === 'success' ? (
        <CheckoutSuccessCard plan={flow.plan} workspace={workspace} />
      ) : null}
      {flow.view === 'form' ? (
        <>
          <section
            data-enter=""
            aria-labelledby="pick-h"
            className="flex min-w-0 shrink grow basis-115 flex-col gap-5.5"
          >
            <div className="flex flex-col gap-2.5">
              <h1 id="pick-h" className="font-semibold text-checkout">
                {flow.isChange ? 'Change plan' : 'Pick a plan'}
              </h1>
              <p className="text-muted">{introText(workspace, flow.isChange)}</p>
            </div>
            {error ? <ErrorState title="Plans did not load" onRetry={refetch} /> : null}
            {isLoading ? (
              <div className="flex flex-col gap-3">
                <Skeleton isPulsing className="h-40 rounded-card" />
                <Skeleton isPulsing className="h-40 rounded-card" />
              </div>
            ) : null}
            {paidPlans.length > 0 ? (
              <fieldset className="flex flex-col gap-3">
                <legend className="sr-only">Plan</legend>
                {paidPlans.map((plan) => (
                  <PlanOption
                    key={plan.id}
                    plan={plan}
                    isChecked={plan.id === flow.plan}
                    isCurrent={flow.isChange && plan.id === flow.currentPlan}
                    onPick={() => flow.pickPlan(plan.id === 'TEAM' ? 'TEAM' : 'PRO')}
                  />
                ))}
              </fieldset>
            ) : null}
            <p className="text-muted">
              Need Macs reserved for your company, SSO or invoices on contract?{' '}
              <a href={LINKS.ENTERPRISE_MAIL} className="underline underline-offset-3">
                Talk to us about Enterprise
              </a>
              .
            </p>
          </section>
          <CheckoutSummaryCard workspace={workspace} flow={flow} />
        </>
      ) : null}
    </div>
  );
}
